/**
 * The little board, top right, of what is still running out of sight.
 *
 * An Alpha transfer takes 20-30 s (Alpha's own time, see serviceboxAlphaTransfer) and a
 * ServiceBox deletion goes out through the garage's proxy: both outlive the modal that
 * started them, and until now nothing on screen said they were still going — or that they
 * had finished. A toast says it once, at a moment the user may not be looking; a task
 * stays as long as the work does, and only then turns into its outcome.
 *
 * Tasks are keyed by caller ("alpha:<ordreId>"), so re-running one replaces its line
 * instead of stacking a second.
 */
import { create } from "zustand";

export type BackgroundTaskStatus = "running" | "waiting" | "done" | "error";

export interface BackgroundTask {
  id: string;
  label: string;
  status: BackgroundTaskStatus;
  /** One short line under the label: what it is waiting on, or how it ended. */
  detail?: string;
}

interface BackgroundTaskStore {
  tasks: BackgroundTask[];
}

export const useBackgroundTasks = create<BackgroundTaskStore>(() => ({ tasks: [] }));

// A success can go on its own; a failure has to be readable by someone who looked away.
const DISMISS_AFTER_MS = { done: 5000, error: 15000 } as const;
const timers = new Map<string, ReturnType<typeof setTimeout>>();

function clearTimer(id: string) {
  const timer = timers.get(id);
  if (timer) {
    clearTimeout(timer);
    timers.delete(id);
  }
}

/** Start a task, move it along, or end it. Finished ones clear themselves. */
export function setBackgroundTask(
  id: string,
  update: { label?: string; status: BackgroundTaskStatus; detail?: string },
) {
  useBackgroundTasks.setState((s) => {
    const existing = s.tasks.find((t) => t.id === id);
    const next: BackgroundTask = {
      id,
      label: update.label ?? existing?.label ?? "",
      status: update.status,
      detail: update.detail,
    };
    return {
      tasks: existing ? s.tasks.map((t) => (t.id === id ? next : t)) : [...s.tasks, next],
    };
  });

  clearTimer(id);
  if (update.status === "done" || update.status === "error") {
    timers.set(id, setTimeout(() => dismissBackgroundTask(id), DISMISS_AFTER_MS[update.status]));
  }
}

export function dismissBackgroundTask(id: string) {
  clearTimer(id);
  useBackgroundTasks.setState((s) => ({ tasks: s.tasks.filter((t) => t.id !== id) }));
}
